import { Link } from "react-router-dom";
import { CalendarCheck, Activity, CheckCircle2, Wallet, Star, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";

const today = new Date().toISOString().slice(0, 10);
const daysUntil = (d) => Math.max(0, Math.ceil((new Date(d) - new Date()) / 86400000));

export function CustomerDashboard({ bookings, pending, onGoTrips, onReview }) {
  const { t } = useTranslation();
  if (bookings === null) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}</div>;
  }
  const active = bookings.filter((b) => b.status !== "cancelled");
  const upcoming = active.filter((b) => (b.pickup_date || "") > today);
  const now = active.filter((b) => (b.pickup_date || "") <= today && today <= (b.return_date || ""));
  const completed = active.filter((b) => (b.return_date || "") < today);
  const spent = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.amount_due - b.deposit), 0);
  const next = [...now, ...upcoming].sort((a, b) => (a.pickup_date > b.pickup_date ? 1 : -1))[0];

  const KPIS = [
    { icon: CalendarCheck, label: t("dashboard.upcomingTrips"), value: upcoming.length, testid: "kpi-upcoming" },
    { icon: Activity, label: t("dashboard.activeNow"), value: now.length, testid: "kpi-active" },
    { icon: CheckCircle2, label: t("dashboard.completed"), value: completed.length, testid: "kpi-completed" },
    { icon: Wallet, label: t("dashboard.totalSpent"), value: money(spent), testid: "kpi-spent" },
  ];

  return (
    <div className="space-y-8">
      {next ? (
        <div data-testid="next-trip-hero" className="bg-slate-900 text-white rounded-2xl p-8 relative overflow-hidden shadow-lg">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-blue-600/20 blur-2xl" />
          <span className="bg-blue-600/20 text-blue-300 border border-blue-500/30 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider inline-block mb-4">
            {next.pickup_date <= today ? t("dashboard.tripInProgress") : t("dashboard.nextTrip", { days: daysUntil(next.pickup_date) })}
          </span>
          <h2 className="font-heading text-2xl md:text-3xl font-bold">{next.car_name}</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-slate-300 text-sm">
            <span className="flex items-center gap-1.5"><CalendarDays size={15} />{next.pickup_date} → {next.return_date}</span>
            {next.pickup_location && <span className="flex items-center gap-1.5"><MapPin size={15} />{next.pickup_location}</span>}
          </div>
          <button onClick={onGoTrips} data-testid="hero-view-trip" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-5 py-2.5 text-sm font-semibold transition-colors">
            {t("dashboard.viewTrip")} <ArrowRight size={15} />
          </button>
        </div>
      ) : (
        <div data-testid="no-next-trip" className="bg-slate-900 text-white rounded-2xl p-8">
          <h2 className="font-heading text-2xl font-bold">{t("dashboard.noUpcoming")}</h2>
          <p className="text-slate-300 mt-2 text-sm">{t("dashboard.noUpcomingSub")}</p>
          <Link to="/fleet" className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold hover:bg-blue-700">{t("dashboard.browseFleet")} <ArrowRight size={15} /></Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k) => (
          <div key={k.testid} data-testid={k.testid} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600"><k.icon size={18} /></span>
            <p className="font-heading text-2xl font-bold text-slate-900 mt-3">{k.value}</p>
            <p className="text-sm text-slate-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {pending && pending.length > 0 && (
        <div data-testid="review-pending-nudge" className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 text-amber-900 font-medium">
            <Star size={20} className="fill-amber-400 text-amber-500" />
            {t("dashboard.reviewNudge", { count: pending.length })}
          </div>
          <button onClick={() => onReview({ booking_id: pending[0].booking_id, car_name: pending[0].car_name, counterparty: pending[0].counterparty })}
            data-testid="nudge-review-btn" className="rounded-full bg-amber-500 text-white px-5 py-2 text-sm font-semibold hover:bg-amber-600">
            {t("dashboard.reviewCar", { car: pending[0].car_name })}
          </button>
        </div>
      )}
    </div>
  );
}
